import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Repeat, Trash2, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Avatar, Badge, Button, Card, EmptyState, Modal, PageHeader, Skeleton } from "../components/ui";
import { SHIFT_LABEL, SHIFT_TONE, formatMinutes, isManagerOrAbove } from "../lib/people";
import type { Shift, ShiftTemplate, ShiftTemplateWrite } from "../api/types";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * Roster: expected work, as a weekly board.
 *
 * Rows are employees, columns are the seven days. A cell shows the assigned
 * shift (or OFF, or an approved leave overlay). Managers edit a cell in place;
 * staff see only their own published row. Actual work lives in Attendance --
 * this page never records it.
 */
export function RosterPage() {
  const { user } = useAuth();
  const canManage = isManagerOrAbove(user?.role);
  const queryClient = useQueryClient();

  const [weekStart, setWeekStart] = useState(() => startOfWeek(isoDate(new Date())));
  const [branchFilter, setBranchFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [editing, setEditing] = useState<{ employeeId: string; date: string } | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishNotice, setPublishNotice] = useState<string | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const publishingRef = useRef(false);

  const from = weekStart;
  const to = addDays(weekStart, 6);

  const shifts = useQuery({
    queryKey: ["roster", from, to, branchFilter, departmentFilter, employeeFilter],
    queryFn: ({ signal }) =>
      api.shiftsPage(
        {
          from,
          to,
          employee: employeeFilter || undefined,
          branch: branchFilter || undefined,
          department: departmentFilter || undefined,
          page_size: 500,
        },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const employees = useQuery({
    queryKey: ["employee-options"],
    // Full records, not the directory: the branch and department filters need
    // the ids the API scopes on, and the directory only carries display names.
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    enabled: canManage,
    staleTime: 5 * 60_000,
  });

  const templates = useQuery({
    queryKey: ["shift-templates"],
    queryFn: ({ signal }) => api.shiftTemplatesPage({ page_size: 100 }, signal),
    enabled: canManage,
  });

  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: ({ signal }) => api.branches(signal),
    enabled: canManage,
  });
  const departments = useQuery({
    queryKey: ["departments", 100],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 100 }, signal),
    enabled: canManage,
  });

  const leave = useQuery({
    queryKey: ["roster-leave", from, to],
    queryFn: ({ signal }) =>
      api.leaveRequests(
        { status: "approved", from, to, page_size: 500 },
        signal,
      ),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["roster"] });

  const createShift = useMutation({
    mutationFn: (body: Parameters<typeof api.createShift>[0]) => api.createShift(body),
    onSuccess: invalidate,
  });
  const updateShift = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof api.updateShift>[1] }) =>
      api.updateShift(id, body),
    onSuccess: invalidate,
  });
  const removeShift = useMutation({
    mutationFn: (id: string) => api.deleteShift(id),
    onSuccess: invalidate,
  });
  const publishShift = useMutation({
    mutationFn: (id: string) => api.publishShift(id),
    onSuccess: invalidate,
  });

  const draftShifts = (shifts.data?.results ?? []).filter((shift) => shift.status === "draft");

  const publishWeek = useMutation({
    mutationFn: (drafts: Shift[]) => publishDraftShifts(drafts, api.publishShift),
    onSuccess: () => {
      publishingRef.current = false;
      setConfirmPublish(false);
      setPublishError(null);
      setPublishNotice("Roster published successfully.");
      invalidate();
    },
    onError: (caught) => {
      publishingRef.current = false;
      setPublishNotice(null);
      setPublishError(errorMessage(caught));
      // A partial failure has already published some shifts; refresh so the
      // remaining drafts are the only thing a retry can send.
      invalidate();
    },
  });

  function openPublishConfirmation() {
    if (publishingRef.current || publishWeek.isPending || draftShifts.length === 0) return;
    setPublishError(null);
    setPublishNotice(null);
    setConfirmPublish(true);
  }

  function closePublishConfirmation() {
    if (publishingRef.current || publishWeek.isPending) return;
    setConfirmPublish(false);
    setPublishError(null);
  }

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(from, i)), [from]);

  const allRows = employees.data?.results ?? [];
  const visibleRows = allRows.filter(
    (person) =>
      person.status !== "exited" &&
      (!branchFilter || person.branch === branchFilter) &&
      (!departmentFilter || person.department === departmentFilter),
  );

  const shiftMap = useMemo(() => {
    const map = new Map<string, Shift>();
    for (const shift of shifts.data?.results ?? []) {
      map.set(`${shift.employee}:${shift.shift_date}`, shift);
    }
    return map;
  }, [shifts.data]);

  const leaveMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const request of leave.data?.results ?? []) {
      // The API is asked for approved leave, and this guard keeps a mixed
      // response from ever turning a pending/rejected/cancelled request into
      // an approved overlay.
      if (request.status !== "approved") continue;
      let day = request.from_date;
      while (day <= request.to_date) {
        map.set(`${request.employee}:${day}`, request.leave_type_name ?? "Leave");
        day = addDays(day, 1);
      }
    }
    return map;
  }, [leave.data]);

  // Coverage is counted from the roster rows actually on screen: who is on a
  // shift, off, on leave, or has nothing at all. Deliberately counts, not a
  // percentage -- we have no required-staffing figure, and a made-up "80%
  // covered" would be a number nobody could act on.
  const dayStats = useMemo(
    () =>
      days.map((day) => {
        let onShift = 0;
        let off = 0;
        let leave = 0;
        let notRostered = 0;
        for (const person of visibleRows) {
          const shift = shiftMap.get(`${person.id}:${day}`);
          const onLeave = leaveMap.get(`${person.id}:${day}`);
          if (onLeave) leave += 1;
          else if (shift?.is_off) off += 1;
          else if (shift) onShift += 1;
          else notRostered += 1;
        }
        return { day, onShift, off, leave, notRostered };
      }),
    [days, visibleRows, shiftMap, leaveMap],
  );
  const uncoveredTotal = dayStats.reduce((sum, stat) => sum + stat.notRostered, 0);

  function assign(employeeId: string, date: string, template: ShiftTemplate | null, off: boolean) {
    const existing = shiftMap.get(`${employeeId}:${date}`);
    if (off) {
      const body = {
        employee: employeeId,
        shift_date: date,
        start_time: "00:00",
        end_time: "00:00",
        is_off: true,
        template: null,
      };
      if (existing) updateShift.mutate({ id: existing.id, body });
      else createShift.mutate(body);
    } else if (template) {
      const body = {
        employee: employeeId,
        shift_date: date,
        start_time: template.start_time,
        end_time: template.end_time,
        break_minutes: template.break_minutes,
        template: template.id,
        is_off: false,
      };
      if (existing) updateShift.mutate({ id: existing.id, body });
      else createShift.mutate(body);
    } else if (existing) {
      removeShift.mutate(existing.id);
    }
    setEditing(null);
  }

  const error =
    shifts.error ?? createShift.error ?? updateShift.error ?? removeShift.error;

  // An employee has no employee directory and no edit rights: they see their
  // own published rota as a simple list, not the manager's assignment board.
  if (!canManage) {
    const myShifts = [...(shifts.data?.results ?? [])].sort((a, b) =>
      a.shift_date < b.shift_date ? -1 : 1,
    );
    const myLeave = (leave.data?.results ?? []).filter(
      (request) => request.status === "approved",
    );
    return (
      <>
        <PageHeader
          title="Roster"
          subtitle="Your published shifts."
          actions={
            <>
              <Button onClick={() => setWeekStart(addDays(weekStart, -7))}>
                <ChevronLeft size={15} /> Prev
              </Button>
              <Button onClick={() => setWeekStart(startOfWeek(isoDate(new Date())))}>
                This week
              </Button>
              <Button onClick={() => setWeekStart(addDays(weekStart, 7))}>
                Next <ChevronRight size={15} />
              </Button>
            </>
          }
        />
        <p className="mb-3 text-sm text-slate-600">
          {formatDay(from)} – {formatDay(to)}
        </p>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {shifts.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : shifts.isError ? (
            <EmptyState title="Could not load your roster" body="Please try again." />
          ) : myShifts.length === 0 ? (
            <EmptyState
              title="No published shifts this week"
              body="Your rota appears here once your manager publishes it."
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Date</th>
                  <th className="px-4 py-2.5">Shift</th>
                  <th className="px-4 py-2.5">Hours</th>
                  <th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myShifts.map((shift) => (
                  <tr key={shift.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-sm text-slate-700">{formatDay(shift.shift_date)}</td>
                    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-600">
                      {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                      {shift.spans_midnight ? (
                        <span className="ml-2 text-xs text-sky-600">overnight</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-sm tabular-nums text-slate-700">
                      {formatMinutes(shift.duration_minutes)}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge tone={SHIFT_TONE[shift.status ?? "draft"]}>
                        {SHIFT_LABEL[shift.status ?? "draft"]}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {myLeave.length ? (
          <Card className="mt-4">
            <h2 className="mb-2 text-sm font-semibold text-slate-900">
              Approved leave
            </h2>
            <ul className="space-y-1.5">
              {myLeave.map((request) => (
                <li
                  key={request.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="text-slate-700">
                    {formatDay(request.from_date)} - {formatDay(request.to_date)}
                  </span>
                  <span className="text-sky-600">
                    {`${request.leave_type_name || "Leave"} (${request.days} day${
                      Number(request.days) === 1 ? "" : "s"
                    })`}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Roster"
        subtitle={
          canManage
            ? "Expected work. Click a cell to assign a shift, mark OFF, or clear it."
            : "Your published shifts."
        }
        actions={
          canManage ? (
            <>
              <Button onClick={() => setShowTemplates((v) => !v)}>
                {showTemplates ? <X size={14} /> : <Repeat size={14} />} Shift templates
              </Button>
              <Button
                variant="primary"
                disabled={draftShifts.length === 0 || publishWeek.isPending}
                onClick={openPublishConfirmation}
                title="Publish every draft shift this week so staff can see their rota"
              >
                Publish week{draftShifts.length ? ` (${draftShifts.length})` : ""}
              </Button>
              <Link to="/people/attendance">
                <Button variant="secondary">Attendance →</Button>
              </Link>
            </>
          ) : null
        }
      />

      {publishNotice ? (
        <p
          role="status"
          className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
        >
          {publishNotice}
        </p>
      ) : null}

      {publishError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {publishError}
        </p>
      ) : null}

      {showTemplates && canManage ? (
        <TemplatesPanel
          templates={templates.data?.results ?? []}
          employees={allRows}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ["shift-templates"] })}
        />
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {canManage ? (
          <>
            <Button onClick={() => setWeekStart(addDays(weekStart, -7))}>
              <ChevronLeft size={15} /> Prev
            </Button>
            <Button onClick={() => setWeekStart(startOfWeek(isoDate(new Date())))}>This week</Button>
            <Button onClick={() => setWeekStart(addDays(weekStart, 7))}>
              Next <ChevronRight size={15} />
            </Button>
            <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className={selectClass}>
              <option value="">All branches</option>
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All departments</option>
              {(departments.data?.results ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
            <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} className={selectClass}>
              <option value="">All employees</option>
              {allRows.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <span className="ml-auto text-sm text-slate-600">
          {formatDay(from)} – {formatDay(to)}
          {canManage && uncoveredTotal > 0 ? (
            <span className="ml-2 font-medium text-amber-600">
              · {uncoveredTotal} unrostered
            </span>
          ) : null}
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        {shifts.isPending || (canManage && employees.isPending) ? (
          <Skeleton className="h-64 w-full" />
        ) : error ? (
          <EmptyState
            title="Could not load the roster"
            body={error instanceof ApiError ? error.message : "Please try again."}
          />
        ) : visibleRows.length === 0 ? (
          <EmptyState
            title="No employees to roster"
            body="Add employees, or clear the branch and department filters."
          />
        ) : (
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  Employee
                </th>
                {days.map((day, index) => (
                  <th key={day} className="px-2 py-2 text-center text-xs font-medium text-slate-500">
                    <span className="block">{DAY_NAMES[index]}</span>
                    <span className="block font-normal text-slate-400">{formatDay(day)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleRows.map((person) => (
                <tr key={person.id}>
                  <td className="sticky left-0 z-10 bg-white px-3 py-2">
                    <Link to={`/people/employees/${person.id}`} className="flex items-center gap-2">
                      <Avatar name={person.display_name} src={person.photo} size={28} />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {person.display_name}
                        </span>
                        <span className="block truncate text-[11px] text-slate-500">
                          {person.designation || "—"}
                        </span>
                      </span>
                    </Link>
                  </td>
                  {days.map((day) => {
                    const shift = shiftMap.get(`${person.id}:${day}`);
                    const onLeave = leaveMap.get(`${person.id}:${day}`);
                    return (
                      <td key={day} className="p-1 align-top">
                        <button
                          disabled={!canManage}
                          onClick={() => canManage && setEditing({ employeeId: person.id, date: day })}
                          className={cellClass(Boolean(shift?.is_off), Boolean(onLeave), canManage, Boolean(shift?.has_conflict))}
                          title={shift?.has_conflict ? "Conflicts with another shift" : undefined}
                        >
                          <CellLabel shift={shift} leaveName={onLeave} />
                        </button>
                        {canManage && shift && shift.status === "draft" ? (
                          <button
                            type="button"
                            className="mt-1 block w-full text-[10px] font-medium text-slate-600 hover:text-slate-900"
                            onClick={(event) => {
                              event.stopPropagation();
                              publishShift.mutate(shift.id);
                            }}
                          >
                            Publish
                          </button>
                        ) : null}
                        {canManage && shift && shift.status === "published" ? (
                          <button
                            type="button"
                            className="mt-1 block w-full text-[10px] font-medium text-slate-600 hover:text-slate-900"
                            onClick={(event) => {
                              event.stopPropagation();
                              api.unpublishShift(shift.id).then(() => invalidate());
                            }}
                          >
                            Unpublish
                          </button>
                        ) : null}
                        {shift?.has_conflict ? (
                          <span className="mt-1 block text-[10px] font-medium text-amber-600">
                            conflict
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
                <td className="sticky left-0 z-10 bg-slate-50 px-3 py-2 font-medium text-slate-600">
                  Coverage
                </td>
                {dayStats.map((stat) => (
                  <td key={stat.day} className="px-2 py-2 text-center align-top">
                    <span className="block font-medium text-slate-700">
                      {stat.onShift} on shift
                    </span>
                    <span
                      className={`block ${stat.notRostered > 0 ? "text-amber-600" : "text-slate-400"}`}
                      title="No shift, leave or off assigned"
                    >
                      {stat.notRostered} unrostered
                    </span>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {confirmPublish ? (
        <Modal title="Confirm roster publication" onClose={closePublishConfirmation}>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              You are about to publish the roster for{" "}
              <span className="font-medium text-slate-900">
                {formatWeekRange(from, to)}
              </span>
              . Employees will be able to view their assigned shifts after
              publishing.
            </p>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <p>
                {draftShifts.length} draft shift
                {draftShifts.length === 1 ? "" : "s"} will be published.
              </p>
              {draftShifts.some((shift) => shift.has_conflict) ? (
                <p className="mt-1 text-amber-700">
                  {draftShifts.filter((shift) => shift.has_conflict).length} draft
                  shift
                  {draftShifts.filter((shift) => shift.has_conflict).length === 1
                    ? " is"
                    : "s are"}{" "}
                  flagged with a scheduling conflict. Review the highlighted cells
                  before publishing.
                </p>
              ) : null}
            </div>
            {publishError ? (
              <p
                role="alert"
                className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {publishError}
              </p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                onClick={closePublishConfirmation}
                disabled={publishWeek.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (publishingRef.current || publishWeek.isPending) return;
                  publishingRef.current = true;
                  publishWeek.mutate(draftShifts);
                }}
                disabled={publishWeek.isPending || draftShifts.length === 0}
              >
                {publishWeek.isPending ? "Publishing…" : "Confirm & Publish"}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {editing ? (
        <AssignBar
          employeeName={allRows.find((p) => p.id === editing.employeeId)?.display_name ?? "Employee"}
          date={editing.date}
          templates={templates.data?.results ?? []}
          onAssign={(template, off) => assign(editing.employeeId, editing.date, template, off)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

function CellLabel({ shift, leaveName }: { shift?: Shift; leaveName?: string }) {
  if (leaveName) {
    return (
      <>
        <span className="block font-medium text-sky-600">Leave</span>
        <span className="block text-[10px] text-sky-600">{leaveName}</span>
        {shift && !shift.is_off ? (
          <span className="block text-[10px] tabular-nums text-slate-500">
            {`${shift.start_time.slice(0, 5)}-${shift.end_time.slice(0, 5)}`}
          </span>
        ) : null}
      </>
    );
  }
  if (shift?.is_off) {
    return (
      <span className="text-slate-400">
        OFF
        {shift.status === "draft" ? (
          <span className="block text-[10px] text-amber-600">draft</span>
        ) : null}
      </span>
    );
  }
  if (shift) {
    return (
      <>
        <span className="block font-medium text-slate-800">
          {shift.template_name || "Shift"}
        </span>
        <span className="block text-[10px] tabular-nums text-slate-500">
          {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
        </span>
        {shift.status === "draft" ? (
          <span className="block text-[10px] text-amber-600">draft</span>
        ) : null}
      </>
    );
  }
  return <span className="text-slate-300">+</span>;
}

function AssignBar({
  employeeName,
  date,
  templates,
  onAssign,
  onClose,
}: {
  employeeName: string;
  date: string;
  templates: ShiftTemplate[];
  onAssign: (template: ShiftTemplate | null, off: boolean) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white p-4 shadow-lg">
      <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-900">
          {employeeName} · {formatDay(date)}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {templates.length === 0 ? (
            <span className="text-xs text-slate-500">
              No templates yet — add one under Shift templates.
            </span>
          ) : (
            templates.map((template) => (
              <Button key={template.id} onClick={() => onAssign(template, false)}>
                {template.name}
              </Button>
            ))
          )}
          <Button onClick={() => onAssign(null, true)}>OFF</Button>
          <Button variant="ghost" onClick={() => onAssign(null, false)}>
            Clear
          </Button>
          <Button variant="ghost" onClick={onClose}>
            <X size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TemplatesPanel({
  templates,
  employees,
  onChanged,
}: {
  templates: ShiftTemplate[];
  employees: { id: string; display_name: string }[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [genFrom, setGenFrom] = useState("");
  const [genTo, setGenTo] = useState("");
  const [genResult, setGenResult] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: {
      name: string;
      start_time: string;
      end_time: string;
      break_minutes?: number;
      grace_minutes?: number;
      days_of_week?: number[];
    }) => api.createShiftTemplate(body),
    onSuccess: onChanged,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ShiftTemplateWrite> }) =>
      api.updateShiftTemplate(id, body),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteShiftTemplate(id),
    onSuccess: onChanged,
  });
  const generate = useMutation({
    mutationFn: ({
      templateId,
      body,
    }: {
      templateId: string;
      body: { employees: string[]; from_date: string; to_date: string };
    }) => api.generateRoster(templateId, body),
    onSuccess: (result) => {
      setGenResult(`Created ${result.created}, skipped ${result.skipped.length}.`);
      onChanged();
    },
  });

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  function collectDays(form: HTMLFormElement): number[] {
    const data = new FormData(form);
    return data
      .getAll("days")
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }

  return (
    <Card className="mb-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Shift templates</h2>
        {!open ? (
          <Button onClick={() => setOpen(true)}>
            <Plus size={14} /> New template
          </Button>
        ) : null}
      </div>
      {templates.length === 0 ? (
        <p className="text-sm text-slate-500">
          No shift templates yet. Create reusable shifts such as Morning, General, Evening
          or Closing, with their break.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {templates.map((template) => (
            <li key={template.id} className="py-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-900">{template.name}</p>
                  <p className="text-xs text-slate-500">
                    {template.start_time.slice(0, 5)}–{template.end_time.slice(0, 5)}
                    {template.spans_midnight ? " (overnight)" : ""} ·{" "}
                    {formatMinutes(template.duration_minutes)}
                    {template.break_minutes ? ` · ${template.break_minutes}m break` : ""}
                    {template.grace_minutes ? ` · ${template.grace_minutes}m grace` : ""}
                    {template.days_of_week && Array.isArray(template.days_of_week) && template.days_of_week.length
                      ? ` · days ${template.days_of_week.join(",")}`
                      : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setGeneratingId((current) => (current === template.id ? null : template.id))
                    }
                  >
                    Generate
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setEditingId((current) => (current === template.id ? null : template.id))
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    title="Delete"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(template.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>

              {editingId === template.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    const form = event.currentTarget;
                    const data = new FormData(form);
                    update.mutate({
                      id: template.id,
                      body: {
                        name: String(data.get("name") ?? "").trim(),
                        start_time: String(data.get("start") ?? template.start_time),
                        end_time: String(data.get("end") ?? template.end_time),
                        break_minutes: Number(data.get("brk") ?? template.break_minutes) || 0,
                        grace_minutes: Number(data.get("grace") ?? template.grace_minutes) || 0,
                        days_of_week: collectDays(form),
                      },
                    });
                    setEditingId(null);
                  }}
                  className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-4"
                >
                  <input name="name" required defaultValue={template.name} placeholder="Name" className={inputClass} />
                  <input name="start" type="time" defaultValue={template.start_time.slice(0, 5)} className={inputClass} />
                  <input name="end" type="time" defaultValue={template.end_time.slice(0, 5)} className={inputClass} />
                  <input name="brk" type="number" min={0} defaultValue={template.break_minutes} placeholder="Break (min)" className={inputClass} />
                  <input name="grace" type="number" min={0} defaultValue={template.grace_minutes} placeholder="Grace (min)" className={inputClass} />
                  <div className="sm:col-span-4 flex flex-wrap gap-2">
                    {dayNames.map((label, index) => (
                      <label key={label} className="flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          name="days"
                          value={index}
                          defaultChecked={Array.isArray(template.days_of_week) && template.days_of_week.includes(index)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {update.error instanceof ApiError ? (
                    <p role="alert" className="text-sm text-rose-600 sm:col-span-4">{update.error.message}</p>
                  ) : null}
                  <div className="flex justify-end gap-2 sm:col-span-4">
                    <Button onClick={() => setEditingId(null)}>Cancel</Button>
                    <Button type="submit" variant="primary" disabled={update.isPending}>
                      Save
                    </Button>
                  </div>
                </form>
              ) : null}

              {generatingId === template.id ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    generate.mutate({
                      templateId: template.id,
                      body: {
                        employees: employees.map((employee) => employee.id),
                        from_date: genFrom,
                        to_date: genTo,
                      },
                    });
                  }}
                  className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-3"
                >
                  <input
                    type="date"
                    value={genFrom}
                    onChange={(event) => setGenFrom(event.target.value)}
                    className={inputClass}
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={genTo}
                    onChange={(event) => setGenTo(event.target.value)}
                    className={inputClass}
                    placeholder="To"
                  />
                  <Button type="submit" variant="primary" disabled={generate.isPending}>
                    Generate roster
                  </Button>
                  {genResult ? <p className="text-sm text-slate-600 sm:col-span-3">{genResult}</p> : null}
                  {generate.error instanceof ApiError ? (
                    <p role="alert" className="text-sm text-rose-600 sm:col-span-3">{generate.error.message}</p>
                  ) : null}
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const data = new FormData(form);
            create.mutate({
              name: String(data.get("name") ?? "").trim(),
              start_time: String(data.get("start") ?? "09:00"),
              end_time: String(data.get("end") ?? "18:00"),
              break_minutes: Number(data.get("brk") ?? 0) || 0,
              grace_minutes: Number(data.get("grace") ?? 0) || 0,
              days_of_week: collectDays(form),
            });
            form.reset();
            setOpen(false);
          }}
          className="mt-3 grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-4"
        >
          <input name="name" required placeholder="Name (e.g. General)" className={inputClass} />
          <input name="start" type="time" defaultValue="10:00" className={inputClass} />
          <input name="end" type="time" defaultValue="19:00" className={inputClass} />
          <input name="brk" type="number" min={0} defaultValue={60} placeholder="Break (min)" className={inputClass} />
          <input name="grace" type="number" min={0} defaultValue={0} placeholder="Grace (min)" className={inputClass} />
          <div className="sm:col-span-4 flex flex-wrap gap-2">
            {dayNames.map((label, index) => (
              <label key={label} className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" name="days" value={index} />
                {label}
              </label>
            ))}
          </div>
          {create.error instanceof ApiError ? (
            <p role="alert" className="text-sm text-rose-600 sm:col-span-4">{create.error.message}</p>
          ) : null}
          <div className="flex justify-end gap-2 sm:col-span-4">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              Save template
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}

function cellClass(off: boolean, onLeave: boolean, clickable: boolean, conflict: boolean = false): string {
  const base = "block h-12 w-full rounded px-1 py-1 text-center text-xs leading-tight";
  const tone = off
    ? "bg-slate-50 text-slate-400"
    : onLeave
      ? "bg-sky-50 text-sky-600"
      : conflict
        ? "bg-amber-50 hover:bg-amber-100"
        : "bg-slate-50 hover:bg-slate-100";
  return `${base} ${tone} ${clickable ? "cursor-pointer" : "cursor-default"}`;
}

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
const selectClass =
  "rounded-md border-0 px-2 py-1.5 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";

function isoDate(value: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function addDays(iso: string, days: number): string {
  const value = new Date(`${iso}T00:00:00`);
  value.setDate(value.getDate() + days);
  return isoDate(value);
}

function startOfWeek(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  const offset = (value.getDay() + 6) % 7;
  value.setDate(value.getDate() - offset);
  return isoDate(value);
}

function formatDay(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return value.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatRangeDay(iso: string): string {
  const value = new Date(`${iso}T00:00:00`);
  return value.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function formatWeekRange(from: string, to: string): string {
  return `${formatRangeDay(from)} - ${formatRangeDay(to)}`;
}

/**
 * Publish every draft shift in a week and fail as one unit.
 *
 * A partial failure still refreshes the board from the caller, so retrying
 * sends only the drafts the API did not accept. The API is called once per
 * draft; the caller's pending state is what prevents a duplicate submission.
 */
export async function publishDraftShifts(
  drafts: Pick<Shift, "id">[],
  publish: (id: string) => Promise<unknown>,
): Promise<void> {
  if (drafts.length === 0) {
    throw new Error("There are no draft shifts to publish.");
  }
  const results = await Promise.allSettled(
    drafts.map((shift) => publish(shift.id)),
  );
  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === "rejected",
  );
  if (failures.length === 0) return;

  if (
    failures.length === 1 &&
    drafts.length === 1 &&
    failures[0]?.reason instanceof ApiError
  ) {
    throw failures[0].reason;
  }
  const prefix =
    drafts.length > 1
      ? `${failures.length} of ${drafts.length} draft shifts could not be published. `
      : "";
  throw new Error(`${prefix}${errorMessage(failures[0]?.reason)}`);
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fieldMessages = Object.entries(error.fields).flatMap(
      ([field, messages]) =>
        messages.map((message) => `${field.replace(/_/g, " ")}: ${message}`),
    );
    return fieldMessages.length
      ? `${error.message} ${fieldMessages.join(" ")}`
      : error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Roster could not be published. Please try again.";
}
